import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCrudPermissions, CrudPermissionCodes } from '../../hooks/useCrudPermissions';

type ProtectedFunctionRouteProps = {
  children: React.ReactNode;
  redirectTo?: string;
} & (
  | { functionCode: string; action: 'view' | 'create' | 'edit' | 'delete' | 'import' }
  | { codes: CrudPermissionCodes; require: keyof CrudPermissionCodes }
);

export const ProtectedFunctionRoute: React.FC<ProtectedFunctionRouteProps> = ({
  children,
  redirectTo = '/',
  ...props
}) => {
  const navigate = useNavigate();
  const codes =
    'codes' in props
      ? props.codes
      : {
          view: props.action === 'view' ? props.functionCode : undefined,
          create: props.action === 'create' ? props.functionCode : undefined,
          edit: props.action === 'edit' ? props.functionCode : undefined,
          delete: props.action === 'delete' ? props.functionCode : undefined,
          import: props.action === 'import' ? props.functionCode : undefined,
        };

  const requireKey = 'require' in props ? props.require : ('action' in props ? (props.action === 'import' ? 'import' : props.action) : 'view');
  const permissions = useCrudPermissions(codes);

  const allowed =
    requireKey === 'view'
      ? permissions.canView
      : requireKey === 'create'
        ? permissions.canCreate
        : requireKey === 'edit'
          ? permissions.canEdit
          : requireKey === 'delete'
            ? permissions.canDelete
            : permissions.canImport;

  useEffect(() => {
    if (!permissions.loading && !allowed) {
      navigate(redirectTo);
    }
  }, [permissions.loading, allowed, navigate, redirectTo]);

  if (permissions.loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500" />
      </div>
    );
  }

  if (!allowed) {
    return null;
  }

  return <>{children}</>;
};
